import { List, Typography, styled } from '@mui/material'
import startCase from 'lodash-es/startCase.js'
import type { CustomBend, Finish, Handing, RawMaterial } from 'metalworks-domain'
import { Hole, Plate, featureLabels, finishLabels, rawMaterialLabels, round } from 'metalworks-domain'
import { useObservableEagerState } from 'observable-hooks'
import { useMemo } from 'react'
import { map } from 'rxjs'

import { ListItem, PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../../systems/configurator-interface'
import { NonStandardMaterialWarning } from '../NonStandardMaterialWarning'

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}))

const StyledList = styled(List)(({ theme }) => ({
  display: 'grid',
  gridAutoRows: 'minmax(0, 1fr)',

  '& > li + li': {
    borderTop: '1px solid',
    borderColor: theme.palette.divider,
  },
}))

interface SummaryListItemProps {
  title: string
  description: string
  quantity: number
}

const SummaryListItem = (props: SummaryListItemProps) => {
  const { title, description, quantity } = props

  const fullTitle = `${quantity}x ${title}`

  return (
    <ListItem.StaticLayout
      title={
        <Typography variant="body1" title={fullTitle}>
          {fullTitle}
        </Typography>
      }
      subtitle={
        <Subtitle variant="body2" title={description}>
          {description}
        </Subtitle>
      }
      primarySlot={null}
      disableGutters
    />
  )
}

interface Summary {
  plate: {
    width: number
    height: number
    thickness: number
    rawMaterial: RawMaterial
    finish: Finish
    finishDirection: string
    handing: Handing
    standard: boolean
    bend: CustomBend | null
  } | null
  items: Array<{
    title: string
    description: string
    quantity: number
  }>
}

// TODO: this is just placeholder example - we should think about whether it makes sense to have this in sdk or if summaries
// are tooo custom to be handled by sdk
export const SummaryPage = () => {
  const { storeSystem, domain } = configuratorInterface.useDomain()

  const summary$ = useMemo(() => {
    return storeSystem.stateSlices.committed.pipe(
      map(emission => {
        return emission.state.entities.reduce<Summary>(
          (acc, entity) => {
            if (Plate.is(entity)) {
              const $plate = domain.getRoot$Entity(emission.state)

              const selectedCustomMaterial = $plate.$selectedCustomMaterial
              if (!selectedCustomMaterial) {
                throw new Error('Custom material not found. This should happen only in dry-run mode.')
              }

              acc.plate = {
                bend: $plate.$customBend,
                width: $plate.$sizeX,
                height: $plate.$sizeY,
                thickness: $plate.thickness,
                rawMaterial: $plate.rawMaterial,
                finish: $plate.finish,
                finishDirection: $plate.finishDirection,
                handing: $plate.handing,
                standard: selectedCustomMaterial.standard,
              }

              acc.items.unshift({
                title: startCase(entity.type),
                description: `${round($plate.$sizeX, 2)} x ${round($plate.$sizeY, 2)} x ${$plate.thickness} mm`,
                quantity: 1,
              })
            }

            if (Hole.is(entity)) {
              const title = featureLabels[entity.type]
              const existingBomItem = acc.items.find(bomItem => bomItem.title === title)

              if (existingBomItem) {
                existingBomItem.quantity += 1
              } else {
                acc.items.push({
                  title,
                  description: '',
                  quantity: 1,
                })
              }
            }

            return acc
          },
          {
            plate: null,
            items: [],
          }
        )
      })
    )
  }, [storeSystem.stateSlices.committed, domain])

  // This can be potentially out of sync => if there are issues, use useStoreSelector instead
  const summary = useObservableEagerState(summary$)

  return (
    <PropertyContainer.Section title="Summary">
      <Typography variant="body2" color="secondary" sx={{ marginTop: 'auto' }}>
        Prices are per plate and do not include GST or shipping, Final pricing is determined on checkout in Daveweb. A
        surcharge may be applied for small orders, or a discount given for bulk orders.
      </Typography>
      <StyledList>
        {summary.plate && (
          <>
            <ListItem.StaticLayout
              title={`${round(summary.plate.width, 2)} mm`}
              subtitle="Width"
              primarySlot={null}
              disableGutters
            />
            <ListItem.StaticLayout
              title={`${round(summary.plate.height, 2)} mm`}
              subtitle="Height"
              primarySlot={null}
              disableGutters
            />
            {summary.plate.bend && (
              <>
                <ListItem.StaticLayout
                  title={`${round(summary.plate.bend.startInnerLength, 2)} mm`}
                  subtitle="Arm length (inner)"
                  primarySlot={null}
                  disableGutters
                />
                <ListItem.StaticLayout
                  title={`${round(summary.plate.bend.endInnerLength, 2)} mm`}
                  subtitle="Arm length (inner)"
                  primarySlot={null}
                  disableGutters
                />
              </>
            )}
            <ListItem.StaticLayout
              title={`${summary.plate.thickness} mm`}
              subtitle="Thickness"
              primarySlot={null}
              disableGutters
            />
            <ListItem.StaticLayout
              title={rawMaterialLabels[summary.plate.rawMaterial]}
              subtitle="Material"
              primarySlot={null}
              disableGutters
            />
            <ListItem.StaticLayout
              title={finishLabels[summary.plate.finish]}
              subtitle="Finish"
              primarySlot={null}
              disableGutters
            />
            <ListItem.StaticLayout
              title={startCase(summary.plate.finishDirection)}
              subtitle="Finish Direction"
              primarySlot={null}
              disableGutters
            />
            <ListItem.StaticLayout
              title={startCase(summary.plate.handing)}
              subtitle="Handing"
              primarySlot={null}
              disableGutters
            />
          </>
        )}
        {summary.items.map(bomItem => (
          <SummaryListItem
            key={bomItem.description}
            title={bomItem.title}
            description={bomItem.description}
            quantity={bomItem.quantity}
          />
        ))}
      </StyledList>
      {summary.plate?.standard === false && <NonStandardMaterialWarning />}
    </PropertyContainer.Section>
  )
}
